import Box from "@mui/material/Box";
import Typography, { TypographyProps } from "@mui/material/Typography";
import React, { CSSProperties, forwardRef } from "react";
import { useLocale } from "designSystem/locale";

export interface TextProps extends Omit<TypographyProps, "children"> {
	/** indicates if text should occupy own line */
	block?: boolean;

	/** indicates if text is bold */
	bold?: boolean;

	/** text block should grow to fill remaining space (flexGrow: 1) */
	grow?: boolean;

	/** line height in pixels */
	lineHeight?: number;

	/** force text to lowercase */
	lowercase?: boolean;

	/** localization resource to use to lookup text; can be simple resource or type resource (i.e. "User.Name") */
	res?: string;

	/** parameters to pass if looking up resource */
	resParams?: Array<number | string>;

	/** indicates if text is semi-bold */
	semiBold?: boolean;

	/** font size (in pixels) */
	size?: number;

	/** text to display */
	text?: number | string;

	/** force text to uppercase */
	uppercase?: boolean;
}

const newline = "\n";
const newlineRegexp = /(\r\n|\n|\\n)/g;
export const SPACE = "\u00A0";

const Text = forwardRef<HTMLElement, TextProps>((props, ref) => {
	const {
		block,
		bold,
		grow,
		lineHeight,
		lowercase,
		noWrap,
		res,
		resParams,
		semiBold,
		size,
		style,
		text,
		uppercase,
		...otherProps
	} = props;

	const styles: CSSProperties = { ...style };

	if (block) {
		styles.display = "block";
	}

	if (grow) {
		styles.flexGrow = 1;
	}

	if (size) {
		styles.fontSize = size;
	}

	if (lineHeight) {
		styles.lineHeight = `${lineHeight}px`;
	}

	if (bold || semiBold) {
		styles.fontWeight = props.bold ? 700 : 600;
	}

	let value = res ? useLocale()
		.resource(res, resParams) : String(text);

	if (uppercase) {
		value = value.toLocaleUpperCase();
	} else if (lowercase) {
		value = value.toLocaleLowerCase();
	}

	if (!noWrap) {
		// check for newlines, and if present, then output one per line
		const a = value.replace(newlineRegexp, newline)
			.split(newline);

		if (a.length > 1) {
			styles.display = "block";

			return (
				<Box>
					{a.map((line, index) => (
						<Typography
							component="span"
							key={index}
							ref={ref}
							style={styles}
							{...otherProps}
						>
							{line || "\u00A0"}
						</Typography>
					))}
				</Box>
			);
		}
	}

	return (
		<Typography
			component="span"
			data-testid="Text"
			noWrap={noWrap}
			ref={ref}
			style={styles}
			{...otherProps}
		>
			{value || SPACE}
		</Typography>
	);
});

export default Text;